import { apiFetch, apiFetchBlob } from "./api";

export interface OrderItemsTextDto {
  quoteId: number;
  bodyHtml: string | null;
  rowVersionBase64: string | null;
}

export const quoteOrderItemsApi = {
  get: (quoteId: number) => apiFetch<OrderItemsTextDto>(`/api/quotes/${quoteId}/order-items-text`),
  put: (quoteId: number, body: { bodyHtml: string | null; rowVersionBase64: string }) =>
    apiFetch<OrderItemsTextDto>(`/api/quotes/${quoteId}/order-items-text`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
};

/**
 * Editor images: the stored HTML keeps stable `/api/quotes/{id}/files/{n}` srcs,
 * but bare <img> tags can't send the Authorization header, so for DISPLAY we swap
 * each src to an object URL fetched via apiFetchBlob, remembering the stable src
 * in data-qf-src. toStorageHtml reverses that before PUT.
 */
export async function toDisplayHtml(
  html: string,
  onObjectUrl?: (url: string) => void,
): Promise<string> {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const imgs = Array.from(doc.querySelectorAll('img[src^="/api/"]'));
  await Promise.all(imgs.map(async (img) => {
    const stable = img.getAttribute("src")!;
    try {
      const blob = await apiFetchBlob(stable);
      const objectUrl = URL.createObjectURL(blob);
      // Report each minted URL to the caller the instant it's created, so the
      // caller can always revoke it — even if it abandons this result (e.g.
      // the component unmounts mid-fetch and never reads the resolved html).
      onObjectUrl?.(objectUrl);
      img.setAttribute("data-qf-src", stable);
      img.setAttribute("src", objectUrl);
    } catch {
      img.remove();
    }
  }));
  return doc.body.innerHTML;
}

export function toStorageHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const img of Array.from(doc.querySelectorAll("img"))) {
    const stable = img.getAttribute("data-qf-src");
    if (stable) {
      img.setAttribute("src", stable);
      img.removeAttribute("data-qf-src");
    } else if ((img.getAttribute("src") ?? "").startsWith("blob:")) {
      img.remove();
    }
  }
  return doc.body.innerHTML;
}
